import React, { useEffect, useRef, useState } from 'react';
import './BuyersEngagementDocuments.scss';
import BuyersEngagementDocumentsSection from './BuyersEngagementDocumentsSection';
import { DocumentSection } from '../../../../../slices/marketplace/timeline/models/document-section.model';
import { EngagementData } from '../../../../../slices/marketplace/timeline/models/engagement-data.model';
import { Button, Label } from 'reactstrap';
import { useStateSelector } from '../../../../../store/selectors';
import { TimelineParty } from '../../../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { ActionDocumentType } from '../../../../../slices/marketplace/timeline/models/enums/action-document-type.enum';
import ButtonLoader from '../../../../../components/Layout/Buttons/ButtonLoader';
import createNotification from '../../../../../utils/createNotification';
import { MarketplaceOpportunityDocument } from '../../../../../slices/marketplace/opportunity/models/opportunity-document';
import { useDispatch } from 'react-redux';
import { GetOpportunity } from '../../../../../slices/marketplace/opportunity/thunks/get-opportunity.thunk';
import { setSelectedOpportunity } from '../../../../../slices/marketplace/opportunity/opportunity.slice';
import { MarketplaceOpportunity } from '../../../../../slices/marketplace/opportunity/models/opportunity';

interface BuyersEngagementDocumentsProps {
    engagementData: EngagementData;
    preSelectedDocumentId: string;
    setPreSelectedDocumentId(docId?: string): void;
    onDocumentDownloaded(documentDetailsId: string): void;
    actionIdForFileUpload: number;
    setShouldUploadDocument(should: boolean): void;
    shouldUploadDocument: boolean;
}

const BuyersEngagementDocuments = (props: BuyersEngagementDocumentsProps) => {
    const dispatch = useDispatch() as any;
    const axios = useStateSelector((state) => state.core.axios);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const targetBuyer = useStateSelector(
        (state) => state.buyersState.targetBuyer
    );
    const opportunity = useStateSelector(
        (t) => t.opportunities.selectedOpportuity
    );

    const documentSections: DocumentSection[] = [
        ...props.engagementData.documentSectionsList,
    ]
        .filter((b) => b.isAvailableForBuyers)
        .sort((a, b) => a.index - b.index);
    const [selectedDocumentsIds, setSelectedDocumentsIds] = useState<string[]>(
        []
    );
    const [isSelectModeEnabled, setIsSelectModeEnabled] =
        useState<boolean>(false);
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const [isGettingForSign, setIsGettingForSign] = useState<boolean>(false);
    const [downloadableDocumentsIds, setDownloadableDocumentsIds] = useState<
        string[][]
    >([]);
    const [reloadDocumentsToggle, setReloadDocumentsToggle] =
        useState<boolean>(false);

    useEffect(() => {
        if (props.shouldUploadDocument) {
            if (fileInputRef.current) {
                fileInputRef.current.click();
                props.setShouldUploadDocument(false);
            }
        }
    }, [props.shouldUploadDocument, fileInputRef.current]);

    const downloadableDocDetailsFilterCondition = (
        documentDetails: MarketplaceOpportunityDocument
    ): boolean => {
        return (
            documentDetails.fileUrl &&
            ((documentDetails.counterParty === TimelineParty.Buyer &&
                (!documentDetails.engagementBuyerId ||
                    documentDetails.engagementBuyerId === targetBuyer.id)) ||
                (documentDetails.counterParty === TimelineParty.Seller &&
                    documentDetails.type ===
                        ActionDocumentType.DocumentWithFeedback))
        );
    };

    const selectSection = () => {
        const areAllAvailableDocumentsSelected = () =>
            downloadableDocumentsIds.reduce(
                (total, row) => total + (row?.length ?? 0),
                0
            ) === selectedDocumentsIds.length;

        const fetchDocuments = async () => {
            const queryParams = selectedDocumentsIds
                .map((url) => `documentDetailsIds=${encodeURIComponent(url)}`)
                .join('&');
            const response = await axios.get(
                `/api/marketplace/${opportunity.opportunityId}/MarketplaceOpportunityDocuments/download-documents?${queryParams}`,
                {
                    responseType: 'blob',
                }
            );
            return response;
        };

        const downloadFiles = async () => {
            setIsDownloading(true);
            const response = await fetchDocuments();
            if (response.status !== 200) {
                createNotification(
                    'An unexpected error occurred while downloading documents',
                    'error'
                );
                setIsDownloading(false);
                return;
            }
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const fileName =
                selectedDocumentsIds.length == 1
                    ? opportunity.engagementData.documentsList.find(
                          (doc) => doc.id == selectedDocumentsIds[0]
                      )?.fileName
                    : 'EngagementDocuments.zip';
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            setSelectedDocumentsIds([]);
            setIsSelectModeEnabled(false);
            setIsDownloading(false);
        };

        return isSelectModeEnabled ? (
            <div className="select-documents-btns-panel">
                <Label className="selected-documents-count">
                    Selected: {selectedDocumentsIds.length}
                </Label>
                <Button
                    className="btn-cancel"
                    onClick={() => {
                        setIsSelectModeEnabled(false);
                        setSelectedDocumentsIds([]);
                    }}>
                    Cancel
                </Button>
                <Button
                    color="primary"
                    onClick={() => {
                        if (areAllAvailableDocumentsSelected()) {
                            setSelectedDocumentsIds([]);
                            return;
                        }
                        setSelectedDocumentsIds(
                            downloadableDocumentsIds.reduce((total, row) =>
                                total.concat(row)
                            )
                        );
                    }}>
                    {areAllAvailableDocumentsSelected()
                        ? 'Unselect All'
                        : 'Select All'}
                </Button>

                <ButtonLoader
                    disabled={selectedDocumentsIds.length === 0}
                    onClick={() => downloadFiles()}
                    buttonText="Download Selected"
                    loaderButtonText={'Download Selected'}
                    isLoading={isDownloading}
                    className={
                        isDownloading
                            ? 'btn btn-primary loader-submit'
                            : 'btn btn-primary'
                    }></ButtonLoader>
            </div>
        ) : documentSections.length === 0 ? (
            <></>
        ) : (
            <Button
                color="primary"
                className="select-documents-btn"
                onClick={() => setIsSelectModeEnabled(true)}>
                Select For Download
            </Button>
        );
    };

    const onDocumentSelected = (documentId: string) => {
        if (isSelectModeEnabled) {
            setSelectedDocumentsIds((prevSelectedDocumentsIds) => {
                if (prevSelectedDocumentsIds.includes(documentId)) {
                    return prevSelectedDocumentsIds.filter(
                        (id) => id !== documentId
                    );
                } else {
                    return [...prevSelectedDocumentsIds, documentId];
                }
            });
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (file) {
            const formData = new FormData();
            formData.append('file', file);

            let tempOpportunityId = opportunity.opportunityId;
            const linkedEv = opportunity.events.find(
                (e) => e.linkedAction?.id === props.actionIdForFileUpload
            );
            axios
                .post(
                    `/api/Marketplace/${opportunity.opportunityId}/MarketplaceOpportunityDocuments/UploadActionDocument/${props.actionIdForFileUpload}`,
                    formData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    }
                )
                .then((response) => {
                    if (response.status !== 200) {
                        const message = (response as any)?.response?.data
                            ?.detail;
                        createNotification(
                            message ??
                                'An error occurred while processing file',
                            'error'
                        );
                    } else {
                        createNotification(
                            'File uploaded successfully',
                            'success'
                        );
                    }
                    fileInputRef.current.value = '';
                    dispatch(GetOpportunity(tempOpportunityId)).then(
                        (oppState: any) => {
                            setReloadDocumentsToggle(!reloadDocumentsToggle);
                            const updatedOpportunity = oppState.payload
                                .opportunity as MarketplaceOpportunity;
                            dispatch(setSelectedOpportunity(tempOpportunityId));
                            const docDetailId =
                                updatedOpportunity.buyer?.actionState.find(
                                    (o) =>
                                        o.actionId ===
                                        props.actionIdForFileUpload
                                )?.documentDetailsId;

                            if (
                                linkedEv?.linkedAction?.referenceItemId ||
                                docDetailId
                            ) {
                                props.setPreSelectedDocumentId(
                                    docDetailId ??
                                        linkedEv.linkedAction.referenceItemId
                                );
                            }
                        }
                    );
                });
        }
    };

    return (
        <div className="buyers-engagement-documents">
            {selectSection()}
            {documentSections.map((section, index) => {
                return (
                    <BuyersEngagementDocumentsSection
                        key={section.id}
                        isSelectModeEnabled={isSelectModeEnabled}
                        selectedDocumentsIds={selectedDocumentsIds}
                        engagementDocumentsSection={section}
                        onDocumentSelected={onDocumentSelected}
                        isGettingForSign={isGettingForSign}
                        setIsGettingForSign={setIsGettingForSign}
                        downloadableDocDetailsFilterCondition={
                            downloadableDocDetailsFilterCondition
                        }
                        preSelectedDocumentId={props.preSelectedDocumentId}
                        resetPreSelectedDocumentId={
                            props.setPreSelectedDocumentId
                        }
                        onDocumentDownload={props.onDocumentDownloaded}
                        setDownloadableDocumentsIds={(ids: string[]) => {
                            setDownloadableDocumentsIds((prevDocs) => {
                                const docs = [...prevDocs];
                                docs[index] = ids;
                                return docs;
                            });
                        }}
                        reloadDocumentsToggle={reloadDocumentsToggle}
                    />
                );
            })}
            {documentSections.length === 0 ? (
                <div className="no-documents">No sections available</div>
            ) : (
                []
            )}

            <input
                hidden={true}
                ref={fileInputRef}
                type="file"
                name="file"
                id="upload-file"
                className="upload-file-input"
                onChange={handleFileChange}
            />
        </div>
    );
};

export default BuyersEngagementDocuments;
